/**
 * Route Definitions
 *
 * Central routing configuration for the entire SPA.
 * Routes are lazy-loaded for optimal performance.
 *
 * Structure:
 * - /login - Public route for authentication
 * - / - Department selector (no sidebar); unknown paths redirect here
 * - /parts, /rental, /supplies - Department-specific routes with sidebars
 *   - Each department has shared pages (directory, quizzes) with department-specific styling
 */

import { Navigate, RouteObject } from 'react-router-dom';
import AuthenticatedLayout from './layouts/AuthenticatedLayout';
import PublicLayout from './layouts/PublicLayout';
import { DepartmentLayout, LastDepartmentLayout } from './layouts/DepartmentLayout';
import { logger } from '@/shared/utils/logger';
import { forceHardReload } from '@/shared/utils/cacheUtils';
import { checkVersionBeforeImport } from '@/shared/utils/versionState';

/**
 * Wraps lazy imports to handle chunk load errors gracefully.
 *
 * KEY IMPROVEMENT: Now checks for version mismatch BEFORE attempting the import.
 * This prevents the 404 error from ever occurring in most cases.
 *
 * Flow:
 * 1. Check versionState for known mismatch (instant, no network)
 * 2. If cache is stale (>15s), do quick version fetch
 * 3. If mismatch detected at either step, reload immediately
 * 4. Only if no mismatch, proceed with import
 * 5. If import fails anyway, fall back to reload with attempt tracking
 *
 * Uses cache-busting reload to ensure the browser fetches fresh files.
 */
async function lazyWithReload<T>(importFn: () => Promise<T>): Promise<T> {
  // STEP 1: Check for version mismatch BEFORE attempting import
  // This is the key improvement - we detect stale chunks before they cause 404s
  try {
    const hasMismatch = await checkVersionBeforeImport();

    if (hasMismatch) {
      logger.critical('Version mismatch detected before import - reloading to get fresh chunks');

      // Clear any existing reload attempts since this is a clean detection
      sessionStorage.removeItem('chunk_reload_attempts');

      // Reload with cache busting
      forceHardReload(false, false);

      // Return a promise that never resolves (page will reload)
      return new Promise<T>(() => {});
    }
  } catch (error) {
    // Version check failed - proceed with import anyway
    // Better to try the import than fail silently
    logger.warn('Pre-import version check failed:', error);
  }

  // STEP 2: Attempt the import
  try {
    return await importFn();
  } catch (error) {
    // STEP 3: Handle import failure
    // Use critical logging to ensure visibility in production
    logger.critical('Lazy load error:', error);

    // Check if this is a chunk load error
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorName = error instanceof Error ? error.name : '';

    const isChunkLoadError =
      errorName === 'ChunkLoadError' ||
      errorMessage.includes('Failed to fetch dynamically imported module') ||
      errorMessage.includes('Importing a module script failed') ||
      errorMessage.includes('Failed to load') ||
      errorMessage.includes('error loading dynamically imported module') ||
      errorMessage.includes('Unable to preload CSS');

    if (isChunkLoadError) {
      // Track reload attempts to prevent infinite loops
      const RELOAD_KEY = 'chunk_reload_attempts';
      const MAX_RELOADS = 2;

      const attempts = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);

      if (attempts < MAX_RELOADS) {
        sessionStorage.setItem(RELOAD_KEY, String(attempts + 1));
        logger.critical(`Chunk load error - attempting cache-busting reload ${attempts + 1}/${MAX_RELOADS}`);

        // Use cache-busting reload instead of simple reload
        // This ensures browser fetches fresh main.js with new chunk hashes
        forceHardReload(false, false);

        // Return a promise that never resolves to prevent further rendering
        return new Promise<T>(() => {});
      } else {
        // Max reloads reached - clear counter and throw error to show message
        logger.critical('Max reload attempts reached. Chunk loading may be blocked by browser extension.');
        sessionStorage.removeItem(RELOAD_KEY);
        // Re-throw to let error boundary handle it
        throw new Error(
          'Unable to load application resources. This may be caused by a browser extension (ad blocker, privacy tools, etc.). ' +
            'Please try disabling browser extensions or use a different browser.'
        );
      }
    }

    // Re-throw other errors
    throw error;
  }
}

export const routes: RouteObject[] = [
  // Public routes (no authentication required)
  {
    path: '/login',
    element: <PublicLayout />,
    children: [
      {
        index: true,
        lazy: () => lazyWithReload(() => import('../pages/LoginPage')),
      },
    ],
  },

  // Protected routes (require authentication)
  {
    path: '/',
    element: <AuthenticatedLayout />,
    children: [
      // Department selector (no sidebar)
      {
        index: true,
        lazy: () => lazyWithReload(() => import('../pages/IndexPage')),
      },

      // Quiz taker pages (top-level URL, sidebar of the last-browsed department).
      // Assignees often lack the `quizzes` page, so these can't live under a department path.
      {
        path: 'quiz',
        element: <LastDepartmentLayout />,
        children: [
          {
            index: true,
            lazy: () => lazyWithReload(() => import('../pages/QuizInboxPage')),
          },
          {
            path: ':assignmentId',
            lazy: () => lazyWithReload(() => import('../pages/QuizTakePage')),
          },
          {
            path: 'step/:stepId',
            lazy: () => lazyWithReload(() => import('../pages/ScenarioStepPage')),
          },
        ],
      },

      // Admin Panel (admin-only access)
      {
        path: 'admin/*',
        lazy: () => lazyWithReload(() => import('../pages/AdminPage/AdminPage')),
      },

      // Parts Department
      {
        path: 'parts',
        element: <DepartmentLayout department="parts" />,
        children: [
          {
            path: 'directory',
            lazy: () => lazyWithReload(() => import('../pages/DirectoryPage')),
          },
          {
            path: 'quizzes',
            lazy: () => lazyWithReload(() => import('../pages/QuizManagePage')),
          },
          {
            path: 'quizzes/:quizId',
            lazy: () => lazyWithReload(() => import('../pages/QuizEditorPage')),
          },
          {
            path: 'quizzes/:quizId/results',
            lazy: () => lazyWithReload(() => import('../pages/QuizResultsPage')),
          },
          {
            path: 'quizzes/runs/:scenarioId',
            lazy: () => lazyWithReload(() => import('../pages/ScenarioDetailPage')),
          },
          {
            path: 'tracks',
            lazy: () => lazyWithReload(() => import('../pages/TracksPage')),
          },
          {
            path: 'bolt-decoder',
            lazy: () => lazyWithReload(() => import('../pages/BoltDecoderPage')),
          },
          {
            path: 'references',
            lazy: () =>
              lazyWithReload(() => import('../pages/DocumentsPage').then((m) => ({ Component: m.PartsReferencesPage }))),
          },
          {
            path: 'vendors',
            lazy: () => lazyWithReload(() => import('../pages/VendorsPage')),
          },
          {
            path: 'backorders',
            lazy: () => lazyWithReload(() => import('../pages/BackordersPage')),
          },
          {
            path: 'label-printer',
            lazy: () => lazyWithReload(() => import('../pages/LabelPrinterPage')),
          },
          {
            path: 'bill-of-lading',
            lazy: () => lazyWithReload(() => import('../pages/BillOfLadingPage')),
          },
        ],
      },

      // Rental Department
      {
        path: 'rental',
        element: <DepartmentLayout department="rental" />,
        children: [
          {
            path: 'directory',
            lazy: () => lazyWithReload(() => import('../pages/DirectoryPage')),
          },
          {
            path: 'quizzes',
            lazy: () => lazyWithReload(() => import('../pages/QuizManagePage')),
          },
          {
            path: 'quizzes/:quizId',
            lazy: () => lazyWithReload(() => import('../pages/QuizEditorPage')),
          },
          {
            path: 'quizzes/:quizId/results',
            lazy: () => lazyWithReload(() => import('../pages/QuizResultsPage')),
          },
          {
            path: 'quizzes/runs/:scenarioId',
            lazy: () => lazyWithReload(() => import('../pages/ScenarioDetailPage')),
          },
          {
            path: 'fuel-calculator',
            lazy: () => lazyWithReload(() => import('../pages/FuelCalculatorPage')),
          },
          {
            path: 'rentals',
            lazy: () => lazyWithReload(() => import('../pages/RentalsPage')),
          },
          {
            path: 'trucking-companies',
            lazy: () => lazyWithReload(() => import('../pages/TruckingCompaniesPage')),
          },
          {
            path: 'trucking-calendar',
            lazy: () => lazyWithReload(() => import('../pages/TruckingCalendarPage')),
          },
          {
            path: 'archive',
            lazy: () => lazyWithReload(() => import('../pages/PhotoArchivePage')),
          },
          {
            path: 'yard',
            lazy: () => lazyWithReload(() => import('../pages/YardDashboardPage')),
          },
          {
            path: 'equipment-search',
            lazy: () => lazyWithReload(() => import('../pages/EquipmentSearchPage')),
          },
          {
            path: 'lost-revenue',
            lazy: () => lazyWithReload(() => import('../pages/LostRevenuePage')),
          },
          {
            path: 'references',
            lazy: () =>
              lazyWithReload(() => import('../pages/DocumentsPage').then((m) => ({ Component: m.RentalReferencesPage }))),
          },
        ],
      },

      // Supplies Department
      {
        path: 'supplies',
        element: <DepartmentLayout department="supplies" />,
        children: [
          {
            path: 'stamps',
            lazy: () => lazyWithReload(() => import('../pages/StampsPage')),
          },
          {
            path: 'references',
            lazy: () =>
              lazyWithReload(() =>
                import('../pages/DocumentsPage').then((m) => ({ Component: m.SuppliesReferencesPage }))
              ),
          },
          {
            path: 'directory',
            lazy: () => lazyWithReload(() => import('../pages/DirectoryPage')),
          },
          {
            path: 'quizzes',
            lazy: () => lazyWithReload(() => import('../pages/QuizManagePage')),
          },
          {
            path: 'quizzes/:quizId',
            lazy: () => lazyWithReload(() => import('../pages/QuizEditorPage')),
          },
          {
            path: 'quizzes/:quizId/results',
            lazy: () => lazyWithReload(() => import('../pages/QuizResultsPage')),
          },
          {
            path: 'quizzes/runs/:scenarioId',
            lazy: () => lazyWithReload(() => import('../pages/ScenarioDetailPage')),
          },
        ],
      },

      // Unknown paths (including the retired /service and /sales departments,
      // whose old links live on in bookmarks, emails and push notifications)
      // go to the department selector instead of the router's raw 404 screen.
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
];
